import * as CertificationWatermark from '@tableau/certification-watermark';
import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell, DataGrid } from '@tableau/data-grid-1-wrapper';
import * as React from 'react';
import * as _ from 'underscore';

import { CommonStyles } from '../../common/react/CommonStyles';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { IconProps, Icons } from '../../common/react/Icons';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { filePathDisplayStringWithBaseNameFirst } from '../../common/utilities/FilePathUtils';
import { L10n } from '../../ts/L10n/L10n';
import { isRemote } from '../../ts/models/ConnectionDetailInfo';
import { IDataConnectionInfo } from '../../ts/models/IDataConnectionInfo';
import { IDataSourceInfo } from '../../ts/models/IDataSourceInfo';
import { DataConnections } from '../../ts/resources/DataConnections';

export namespace ConnectionHoverPanel {

  interface TitleProps {
    count: number;
  }

  interface DetailRowProps {
    data: ConnectionDisplay.Metadata;
  }

  export function shouldDisplayPanel(datasource: IDataSourceInfo) {
    return datasource.dataConnectionsCount > 1;
  }

  function PopoverTitleElement(translate: IReactTranslateService): React.StatelessComponent<TitleProps> {
    return function PopoverTitle(props: TitleProps) {
      const connectionCountString = translate(
        'connectionsCount', { COUNT: props.count }, L10n.Interpolations.Count);
      return <div className="tb-content-item-popover-title">{ connectionCountString }</div>;
    };
  }

  const ConnectionTypeIcons: _.Dictionary<React.ComponentClass<IconProps>> = {
    'tb-icon-datasource-connection-tableau': Icons.dataConnectionTableauServer,
    'tb-icon-datasource-connection-file': Icons.dataConnectionFile,
    'tb-icon-datasource-connection-external': Icons.dataConnectionExternal
  };

  function ConnectionDetailRowElement(translate: IReactTranslateService): React.StatelessComponent<DetailRowProps> {

    return function DataConnectionDetailRow(props: DetailRowProps) {
      const data = props.data;
      const Icon = ConnectionTypeIcons[props.data.iconClass];
      const text = ConnectionDisplay.translatedText(data, translate);
      const rowHeight = CommonStyles.Sizes.CompactRowHeight;

      const ConnectionNameElement = () => data.href
        ? <a href={ data.href }>{ text.connectionName }</a>
        : <span>{ text.connectionName }</span>;

      const ConnectionTypeElement = () =>
        <span style={ { color: CommonStyles.Colors.Grey153} }>{ text.connectionTypeInParentheses }</span>;

      const rowStyle = _.extend({},
        CommonStyles.Layout.TextEllipsis, {
          height: rowHeight,
          lineHeight: rowHeight + 'px',
          cursor: 'default'
        });
      const iconStyle = { height: rowHeight, marginRight: CommonStyles.Sizes.DefaultSpace };

      return (
        <div style={ rowStyle }>
          <Icon title={ text.connectionType } style={ iconStyle } />
          <span title={ text.tooltip }>
            <ConnectionNameElement />&nbsp;<ConnectionTypeElement />
          </span>
        </div>
      );
    };
  }

  export function getPopoverContent(dataConnections: DataConnections,
                                    translate: IReactTranslateService,
                                    siteUrls: SiteUrls
  ): (props: BodyCell.Props) => Promise<JSX.Element> {

    const ConnectionDetailRow = ConnectionDetailRowElement(translate);
    const PopoverTitle = PopoverTitleElement(translate);

    // This helper function returns one row of the popover content.
    function makeConnectionDetailRow(connection: IDataConnectionInfo) {
      const metadata = ConnectionDisplay.getDisplayMetadata(connection, siteUrls);
      return <ConnectionDetailRow data={ metadata } key={ connection.id } />;
    }

    // Return a function that, given the cell props, fetches data and returns the popover content.
    return (props: BodyCell.Props) => {
      return new Promise<JSX.Element>((resolve, reject) => {
        dataConnections.getFromDatasource(props.row.data as IDataSourceInfo)
          .then(connections => {
            resolve(
              <div data-tb-test-id="popover-menu-panel">
                <PopoverTitle count={connections.length} />
                <div style={{ marginTop: CommonStyles.Sizes.DefaultSpace }}>
                  { _.map(connections, makeConnectionDetailRow) }
                </div>
              </div>
            );
          })
          .catch(error => reject(error));
      });
    };
  }
}

// Used for flows
namespace ConnectionDisplay {

  export interface Metadata {
    connectionName: string;
    connectionType: string;
    href?: string;
    iconClass: string;
  }

  export interface TranslatedText {
    connectionName: string;
    nameUnavailable: boolean;
    connectionType: string;
    connectionTypeInParentheses: string;
    tooltip: string;
  }

  export function translatedText(data: Metadata, translate: IReactTranslateService): TranslatedText {
    const connectionNameText = data.connectionName || translate('filenameUnavailable');
    const connectionTypeText = translate('parenthesizedArgument', { ITEM: data.connectionType });

    return {
      connectionName: connectionNameText,
      nameUnavailable: !data.connectionName,
      connectionType: data.connectionType,
      connectionTypeInParentheses: connectionTypeText,
      tooltip: connectionNameText + ' ' + connectionTypeText
    };
  }

  export function getDisplayMetadata(
    connection: IDataSourceInfo | IDataConnectionInfo,
    siteUrls: SiteUrls,
  ): Metadata {
    const details = connection.connectionDetails;
    switch (details.type) {
      case 'tableauserver':
        return {
          connectionName: details.name,
          connectionType: connection.connectionTypeDisplayName,
          href: siteUrls.datasourceUrl(details.id),
          iconClass: 'tb-icon-datasource-connection-tableau'
        };
      case 'tableauserversite':
        return {
          connectionName: details.siteUrl,
          connectionType: connection.connectionTypeDisplayName,
          iconClass: 'tb-icon-datasource-connection-external'
        };
      case 'file':
        return {
          connectionName: filePathDisplayStringWithBaseNameFirst(details.fileName),
          connectionType: connection.connectionTypeDisplayName,
          iconClass: 'tb-icon-datasource-connection-file'
        };
      case 'database':
      case 'webdata-direct':
        if (isRemote(details)) {
          return {
            connectionName: details.serverName,
            connectionType: connection.connectionTypeDisplayName,
            iconClass: 'tb-icon-datasource-connection-external'
          };
        } else {
          return null;
        }
    }
  }
}

export namespace ConnectsToCell {
  const MissingNameStyle: React.CSSProperties = { fontStyle: 'italic', color: CommonStyles.Colors.Grey178 };

  export function cellFormatter(siteUrls: SiteUrls,
                                translate: IReactTranslateService,
                                getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>,
                                hideTooltips?: (data: IDataSourceInfo | IDataConnectionInfo) => boolean
                              ): React.StatelessComponent<BodyCell.Props> {

    return function ConnectsToCell(props: BodyCell.Props): React.ReactElement<any> {

      const dataConnection = props.row.data as IDataSourceInfo | IDataConnectionInfo;
      const metadata = ConnectionDisplay.getDisplayMetadata(dataConnection, siteUrls);

      if (!metadata) {
        return null;
      }

      const translatedText = ConnectionDisplay.translatedText(metadata, translate);
      let additionalConnectionsDisplay: string | undefined = undefined;

      if ((dataConnection as IDataSourceInfo).dataConnectionsCount) {
        const count = (dataConnection as IDataSourceInfo).dataConnectionsCount;
        additionalConnectionsDisplay = additionalConnectionsText(count, translate);
      }

      if (metadata.href) {
        const cellData: DataGridCells.ILink = {
          text: translatedText.connectionName,
          title: translatedText.tooltip,
          url: metadata.href,
          iconClassName: metadata.iconClass,
          iconTitle: translatedText.connectionType,
          style: translatedText.nameUnavailable ? MissingNameStyle : null,
          hideTooltips: hideTooltips && hideTooltips(dataConnection)
        };
        return DataGridCells.LinkWithCount(() => cellData, additionalConnectionsDisplay, getPopoverContent)(props);

      } else {
        const cellData: DataGridCells.ITextWithIcon = {
          text: translatedText.connectionName,
          title: translatedText.tooltip,
          textStyle: translatedText.nameUnavailable ? MissingNameStyle : null,
          iconClass: metadata.iconClass,
          iconTitle: translatedText.connectionType,
          hideTooltips: hideTooltips && hideTooltips(dataConnection)
        };
        return DataGridCells.PlainTextCellWithIconAndCount(() => cellData, getPopoverContent, additionalConnectionsDisplay)(props);
      }
    };
  }

  function additionalConnectionsText(connectionCount: number, translate: IReactTranslateService): string {
    if (connectionCount === 0 || connectionCount === 1) {
      return '';
    }

    return translate('additionalConnectionsPillText',
      { CONNECTION_COUNT: L10n.localizedCount(connectionCount - 1) });
  }
}

export namespace CertifiableConnectsToCell {
  const WrapperStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-start'
  };

  const CertificationMarkIconStyle: React.CSSProperties = { flexShrink: 0 };

  const ConnectsToStyle: React.CSSProperties = { overflowX: 'hidden' };

  export function cellFormatter(
    isCertified: (row: DataGrid.Row) => boolean,
    getTooltipProps: (row: DataGrid.Row) => CertificationWatermark.CertificationTooltipProps,
    siteUrls: SiteUrls,
    translate: IReactTranslateService,
    getPopoverContent: (props: BodyCell.Props) => Promise<JSX.Element>,
    shouldDisplayPanel: (datasource: IDataSourceInfo) => boolean
  ): React.StatelessComponent<BodyCell.Props> {
    return function(props: BodyCell.Props) {
      return (
        <span style={WrapperStyle}>
          <span style={CertificationMarkIconStyle}>
            {React.createElement(DataGridCells.CertificationMarkTooltipCell(isCertified, getTooltipProps), props)}
          </span>
          <span style={ConnectsToStyle}>
            {React.createElement(ConnectsToCell.cellFormatter(siteUrls, translate, getPopoverContent, ConnectionHoverPanel.shouldDisplayPanel), props)}
          </span>
        </span>
      );
    };
  }
}
